/**
 * Submit an idea: every signed-in person, from a dialog the shell owns so the
 * sidebar entry and the Business Opportunities buttons open the same one from any page.
 * Industry is optional: research suggests one when it is left open. The
 * description is markdown, and an idea may cite several source URLs.
 * Submitting queues the research and lands on Business Opportunities, whose row
 * follows the research while it runs.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { AlertTriangle, Lightbulb, RotateCw, Send } from 'lucide-react';

import type { IdeaDetail } from '@/api/types';
import { Modal } from '@/ui/Modal';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { Markdown } from '@/ui/Markdown';

import { formatDate } from './ideaUi';
import { SubmitIdeaFields } from './SubmitIdeaFields';
import { SubmissionSources, industryHint, industryText } from './submissionParts';
import { type IdeaPrefill, type SubmitIdeaForm, useSubmitIdeaForm } from './useSubmitIdeaForm';

const SubmitIdeaContext = createContext<(prefill?: IdeaPrefill) => void>(() => {});

/** Opens the Submit an idea dialog, with its title and description filled in when `prefill` is given. */
export function useOpenSubmitIdea(prefill?: IdeaPrefill): () => void {
  const show = useContext(SubmitIdeaContext);
  // A function that ignores its arguments: it is used as an onClick, which would pass the event.
  return useCallback(() => show(prefill), [show, prefill]);
}

export function SubmitIdeaProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<{ prefill?: IdeaPrefill } | null>(null);
  const show = useCallback((prefill?: IdeaPrefill) => setOpen({ prefill }), []);
  const close = useCallback(() => setOpen(null), []);
  return (
    <SubmitIdeaContext.Provider value={show}>
      {children}
      {open && <SubmitIdeaDialog onClose={close} prefill={open.prefill} />}
    </SubmitIdeaContext.Provider>
  );
}

function SubmitIdeaBody({ form }: { form: SubmitIdeaForm }) {
  const { catalogue } = form;
  if (catalogue.isPending) {
    return (
      <div aria-busy="true" aria-label="Loading the form">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="skeleton-bar users-skeleton-line" />
        ))}
      </div>
    );
  }
  if (catalogue.error) {
    return (
      <div className="alert alert-warning form-alert" role="alert">
        <AlertTriangle aria-hidden="true" />
        <div className="a-body">
          {catalogue.error instanceof Error ? catalogue.error.message : 'The form could not be loaded.'}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void catalogue.refetch()}>
            <RotateCw aria-hidden="true" /> Retry
          </button>
        </div>
      </div>
    );
  }
  return <SubmitIdeaFields form={form} />;
}

function SubmitIdeaDialog({ onClose, prefill }: { onClose: () => void; prefill?: IdeaPrefill }) {
  const form = useSubmitIdeaForm(onClose, prefill);

  return (
    <Modal
      title="Submit an Idea"
      subtitle="Describe a problem or an opportunity; AI research scores it for the idea matrix"
      size="wide"
      onClose={onClose}
      onSubmit={form.onSubmit}
      footer={
        <div className="submit-actions">
          <span className="hint row">
            <Lightbulb aria-hidden="true" className="hint-icon" />
            Research runs in the background. Follow its status in Business Opportunities.
          </span>
          <span className="row">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <ActionButton
              state={buttonState(form.submit)}
              icon={<Send aria-hidden="true" />}
              label="Submit idea"
              pendingLabel="Submitting…"
              doneLabel="Submitted"
              disabled={!form.canSubmit}
            />
          </span>
        </div>
      }
    >
      <SubmitIdeaBody form={form} />
    </Modal>
  );
}

/**
 * What was submitted, in the Submit an idea dialog's own layout with every
 * field read-only: title, industry, who submitted it and when, the
 * description rendered as markdown, and the source URLs (each opens in a new
 * tab). Refinements added later are shown only in History. Opened from an
 * idea's page; submissions are not edited.
 */
export function SubmissionDialog({ idea, onClose }: { idea: IdeaDetail; onClose: () => void }) {
  return (
    <Modal
      title="Submission"
      subtitle="The idea as it was submitted; submissions are not edited"
      size="wide"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="stack-md">
        <div className="field">
          <label htmlFor="submission-title">Title</label>
          <input id="submission-title" type="text" readOnly value={idea.title} />
        </div>

        <div className="grid grid-2">
          <div className="field">
            <label htmlFor="submission-industry">Industry</label>
            <input id="submission-industry" type="text" readOnly value={industryText(idea)} aria-describedby="submission-industry-hint" />
            <span id="submission-industry-hint" className="hint">
              {industryHint(idea)}
            </span>
          </div>
          <div className="field">
            <label htmlFor="submission-author">Submitted by</label>
            <input id="submission-author" type="text" readOnly value={`${idea.author.display_name} on ${formatDate(idea.created_at)}`} />
          </div>
        </div>

        <div className="field">
          <span className="label" id="submission-description">Description</span>
          <div className="readonly-box" aria-labelledby="submission-description">
            {idea.description ? <Markdown>{idea.description}</Markdown> : <p className="hint">No description</p>}
          </div>
        </div>

        <SubmissionSources urls={idea.source_urls} />
      </div>
    </Modal>
  );
}
